/**
 * Font singleton + text→profile expansion. The bundled Inter TTF loads
 * once at boot (non-blocking); text entities expand to closed 'poly'
 * outline loops on the MAIN thread before any kernel sees them — workers
 * never need the font.
 */

import { parseTtf, type ParsedFont } from './ttf';
import type { SketchEntity, Vec2 } from '../timeline/types';

let font: ParsedFont | null = null;
let loading: Promise<void> | null = null;

/** Kick off (or await) loading the bundled font. Safe to call repeatedly. */
export function loadDefaultFont(url = '/fonts/inter.ttf'): Promise<void> {
  loading ??= fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error(`Font fetch failed: ${r.status}`);
      return r.arrayBuffer();
    })
    .then((buf) => {
      font = parseTtf(buf);
    })
    .catch((err: unknown) => {
      loading = null; // allow retry on the next expansion attempt
      throw err;
    });
  return loading;
}

/** Tests / non-browser environments inject the font bytes directly. */
export function setFontFromBuffer(buffer: ArrayBuffer): void {
  font = parseTtf(buffer);
}

export function fontLoaded(): boolean {
  return font !== null;
}

/** Closed outline loops for a string, in mm, origin at the text anchor.
 * `height` is the EM size (cap height ≈ 0.7 × height). */
export function textToLoops(text: string, height: number, at: Vec2): Vec2[][] {
  if (!font) {
    void loadDefaultFont().catch(() => undefined);
    throw new Error('Text font is still loading — the sketch will regenerate; try again in a moment');
  }
  const scale = height / font.unitsPerEm;
  const loops: Vec2[][] = [];
  let penX = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    const g = font.glyphId(code);
    if (g > 0) {
      for (const loop of font.outline(g)) {
        loops.push(loop.map((p) => [at[0] + (penX + p.x) * scale, at[1] + p.y * scale]));
      }
    }
    penX += font.advance(g > 0 ? g : 0) || font.unitsPerEm * 0.3;
  }
  return loops;
}

/**
 * Replace 'text' entities with their outline 'poly' loops. Returns the
 * SAME array reference when there is nothing to expand (cache-friendly).
 * Throws while the font is loading — the feature errors visibly and
 * recovers on the next regeneration.
 */
export function expandTextEntities(
  entities: readonly SketchEntity[],
): readonly SketchEntity[] {
  if (!entities.some((e) => e.kind === 'text')) return entities;
  const out: SketchEntity[] = [];
  for (const e of entities) {
    if (e.kind !== 'text') {
      out.push(e);
      continue;
    }
    for (const loop of textToLoops(e.text, e.height, e.at)) {
      if (loop.length >= 3) out.push({ kind: 'poly', points: loop });
    }
  }
  return out;
}
